import { useState, useMemo } from 'react';
import { InputColumn } from '../parameters-schema';
import { 
  createFirstSplit,
  addSplitToExistingNode,
  deleteBranchFromExistingNode,
  deleteColumnFromTree
} from './tree-manipulation';
import { addSplitToSpecificPath, updateSplitInSpecificPath } from './tree-navigation';

export interface UseDecisionTreeEditorProps<T> {
  table: T;
  inputColumns: InputColumn[];
  decisionTree: any;
  extractPaths: (tree: any) => any[];
  onUpdate: (updatedTable: T) => void;
}

export interface UseDecisionTreeEditorReturn {
  // State
  isLeafEditorOpen: boolean;
  editingPathIndex: number | null;
  editingOutput: any;
  isColumnModalOpen: boolean;
  
  // Paths
  paths: any[];
  
  // Handlers
  handleEditOutput: (rowIndex: number, output: any) => void;
  handleSaveOutput: (newOutput: any) => void;
  handleCreateSplit: (rowIndex: number, colIndex: number, newValue: string) => void;
  handleEditSplit: (rowIndex: number, colIndex: number, newValue: string) => void;
  handleDeleteBranch: (rowIndex: number, colIndex: number) => void;
  handleAddColumn: () => void;
  handleDeleteColumn: (columnIndex: number) => void;
  handleAddNewColumn: (newColumn: InputColumn) => void;
  
  // Modal controls
  closeLeafEditor: () => void;
  closeColumnModal: () => void;
}

export function useDecisionTreeEditor<T>({
  table,
  inputColumns,
  decisionTree,
  extractPaths,
  onUpdate
}: UseDecisionTreeEditorProps<T>): UseDecisionTreeEditorReturn {
  const [isLeafEditorOpen, setIsLeafEditorOpen] = useState(false);
  const [editingPathIndex, setEditingPathIndex] = useState<number | null>(null);
  const [editingOutput, setEditingOutput] = useState<any>(null);
  const [isColumnModalOpen, setIsColumnModalOpen] = useState(false);

  // Extract paths from the decision tree
  const paths = useMemo(() => {
    const extractedPaths = extractPaths(decisionTree);
    return extractedPaths;
  }, [decisionTree, extractPaths]);

  // Handle output editing
  const handleEditOutput = (rowIndex: number, output: any) => {
    setEditingPathIndex(rowIndex);
    setEditingOutput({ ...output });
    setIsLeafEditorOpen(true);
  };

  // Helper function to update a specific leaf node in the tree
  const updateLeafNodeInTree = (node: any, targetPath: any, newOutput: any): any | null => {
    // Deep clone the tree to avoid mutations
    const clonedNode = JSON.parse(JSON.stringify(node));
    
    // Navigate to the target leaf node
    let currentNode: any = clonedNode;
    for (const decision of targetPath.decisions) {
      if (!decision) continue;
      
      switch (decision.type) {
        case 'numerical': {
          if (decision.range === 'default') {
            currentNode = currentNode.split.defaultChild;
          } else {
            const maxValue = decision.range.maxValue;
            const range = currentNode.split.ranges.find((r: any) => r.maxValue === maxValue);
            if (range) {
              currentNode = range.childNode;
            } else {
              return null; // Path not found
            }
          }
          break;
        }
          
        case 'categorical': {
          if (decision.category === 'default') {
            currentNode = currentNode.split.defaultChild;
          } else {
            const category = currentNode.split.categoryMap.find((c: any) => c.category === decision.category);
            if (category) {
              currentNode = category.childNode;
            } else {
              return null; // Path not found
            }
          }
          break;
        }
          
        case 'boolean': {
          if (decision.value === true && currentNode.split.trueChild) {
            currentNode = currentNode.split.trueChild;
          } else {
            currentNode = currentNode.split.falseChild;
          }
          break;
        }
      }
    }
    
    // Update the leaf node's output
    if (currentNode.type === 'issueFlaggerLeaf' || currentNode.type === 'strategyRecommendationLeaf') {
      currentNode.output = newOutput;
      return clonedNode;
    }
    
    return null; // Target node is not a leaf
  };

  const handleSaveOutput = (newOutput: any) => {
    if (editingPathIndex !== null) {
      // Update the specific leaf node in the tree
      const updatedTree = updateLeafNodeInTree(decisionTree, paths[editingPathIndex], newOutput);
      if (updatedTree) {
        onUpdate({
          ...table,
          decisionTree: updatedTree
        });
      }
    }
    setIsLeafEditorOpen(false);
    setEditingPathIndex(null);
    setEditingOutput(null);
  };

  // Column editing handlers - removed as column editing is not supported

  const handleAddColumn = () => {
    setIsColumnModalOpen(true);
  };

  const handleDeleteColumn = (columnIndex: number) => {
    const newColumns = inputColumns.filter((_, index) => index !== columnIndex);
    
    // Use the proper column deletion function to update the tree
    const updatedTree = deleteColumnFromTree(decisionTree, columnIndex);
    
    const updatedTable = {
      ...table,
      inputColumns: newColumns,
      decisionTree: updatedTree
    };
    
    onUpdate(updatedTable);
  };

  const handleAddNewColumn = (newColumn: InputColumn) => {
    const updatedTable = {
      ...table,
      inputColumns: [...inputColumns, newColumn]
    };
    onUpdate(updatedTable);
    setIsColumnModalOpen(false);
  };

  // Split editing functions - using the shared implementation
  const addSplitToSpecificPathLocal = (
    node: any,
    targetPath: any,
    columnIndex: number,
    column: InputColumn,
    newValue: string
  ): any | null => {
    return addSplitToSpecificPath(
      node,
      targetPath,
      columnIndex,
      column,
      newValue,
      createFirstSplit,
      addSplitToExistingNode
    );
  };

  const deleteBranchFromSpecificPath = (
    node: any,
    targetPath: any,
    columnIndex: number
  ): any | null => {
    if (!targetPath || !targetPath.decisions) {
      return node;
    }

    // Create a deep copy to avoid modifying the original
    const deepCopyNode = (node: any): any => {
      if (!node) return node;
      
      if (node.type === 'issueFlaggerLeaf' || node.type === 'strategyRecommendationLeaf') {
        return { ...node };
      }
      
      const newNode = { ...node };
      if (node.split) {
        newNode.split = { ...node.split };
        
        if (node.split.defaultChild) {
          newNode.split.defaultChild = deepCopyNode(node.split.defaultChild);
        }
        
        if (node.split.ranges) {
          newNode.split.ranges = node.split.ranges.map((range: any) => ({
            ...range,
            childNode: deepCopyNode(range.childNode)
          }));
        }
        
        if (node.split.categoryMap) {
          newNode.split.categoryMap = node.split.categoryMap.map((cat: any) => ({
            ...cat,
            childNode: deepCopyNode(cat.childNode)
          }));
        }
        
        if (node.split.trueChild) {
          newNode.split.trueChild = deepCopyNode(node.split.trueChild);
        }
        
        if (node.split.falseChild) {
          newNode.split.falseChild = deepCopyNode(node.split.falseChild);
        }
      }
      
      return newNode;
    };

    const copiedNode = deepCopyNode(node);

    // Find the node that contains the split to delete
    // We need to navigate until we find a node that has a split for the target column
    let parentNode = copiedNode;
    
    // First, try to find the node that contains the split for the target column
    const findNodeWithColumn = (currentNode: any, pathIndex: number): any => {
      if (!currentNode || !currentNode.split) {
        return null;
      }
      
      // If this node has a split for the target column, this is our target node
      if (currentNode.split.columnIndex === columnIndex) {
        return currentNode;
      }
      
      // If we've reached the end of the path, return null
      if (pathIndex >= targetPath.decisions.length) {
        return null;
      }
      
      const decision = targetPath.decisions[pathIndex];
      if (!decision) {
        return null;
      }
      
      // Navigate to the next node based on the decision
      let nextNode = null;
      switch (decision.type) {
        case 'numerical':
          if (decision.range === 'default') {
            nextNode = currentNode.split.defaultChild;
          } else {
            const range = currentNode.split.ranges?.find((r: any) => r.maxValue === decision.range.maxValue);
            nextNode = range?.childNode;
          }
          break;
        case 'categorical':
          if (decision.category === 'default') {
            nextNode = currentNode.split.defaultChild;
          } else {
            const category = currentNode.split.categoryMap?.find((c: any) => c.category === decision.category);
            nextNode = category?.childNode;
          }
          break;
        case 'boolean':
          if (decision.value === true) {
            nextNode = currentNode.split.trueChild;
          } else {
            nextNode = currentNode.split.falseChild;
          }
          break;
      }
      
      if (nextNode) {
        return findNodeWithColumn(nextNode, pathIndex + 1);
      }
      
      return null;
    };
    
    parentNode = findNodeWithColumn(copiedNode, 0);

    // If we found the parent node and it has the split we want to delete
    if (parentNode && parentNode.split && parentNode.split.columnIndex === columnIndex) {
      const updatedParentNode = deleteBranchFromExistingNode(parentNode, targetPath, columnIndex);
      
      // If the parent node was completely removed, we need to replace it in the tree
      if (updatedParentNode !== parentNode) {
        // We need to find the parent of the parentNode and update its reference
        // Since we found parentNode using recursive search, we need to update the tree
        // by replacing the parentNode with updatedParentNode in the copied tree
        
        // Create a function to replace a node in the tree
        const replaceNodeInTree = (currentNode: any, targetNode: any, replacementNode: any): any => {
          if (!currentNode || !currentNode.split) {
            return currentNode;
          }
          
          // Check if any of the children are the target node
          if (currentNode.split.defaultChild === targetNode) {
            return {
              ...currentNode,
              split: {
                ...currentNode.split,
                defaultChild: replacementNode
              }
            };
          }
          
          if (currentNode.split.ranges) {
            const updatedRanges = currentNode.split.ranges.map((range: any) => {
              if (range.childNode === targetNode) {
                return {
                  ...range,
                  childNode: replacementNode
                };
              }
              return range;
            });
            
            if (updatedRanges.some((range: any) => range.childNode === replacementNode)) {
                return {
                  ...currentNode,
                  split: {
                    ...currentNode.split,
                    ranges: updatedRanges
                  }
                };
            }
          }
          
          if (currentNode.split.categoryMap) {
            const updatedCategoryMap = currentNode.split.categoryMap.map((category: any) => {
              if (category.childNode === targetNode) {
                return {
                  ...category,
                  childNode: replacementNode
                };
              }
              return category;
            });
            
            if (updatedCategoryMap.some((category: any) => category.childNode === replacementNode)) {
              return {
                ...currentNode,
                split: {
                  ...currentNode.split,
                  categoryMap: updatedCategoryMap
                }
              };
            }
          }
          
          if (currentNode.split.trueChild === targetNode) {
            return {
              ...currentNode,
              split: {
                ...currentNode.split,
                trueChild: replacementNode
              }
            };
          }
          
          if (currentNode.split.falseChild === targetNode) {
            return {
              ...currentNode,
              split: {
                ...currentNode.split,
                falseChild: replacementNode
              }
            };
          }
          
          // Recursively search in children
          let updatedNode = { ...currentNode };
          if (updatedNode.split) {
            updatedNode.split = { ...updatedNode.split };
            
            if (updatedNode.split.defaultChild) {
              updatedNode.split.defaultChild = replaceNodeInTree(updatedNode.split.defaultChild, targetNode, replacementNode);
            }
            
            if (updatedNode.split.ranges) {
              updatedNode.split.ranges = updatedNode.split.ranges.map((range: any) => ({
                ...range,
                childNode: replaceNodeInTree(range.childNode, targetNode, replacementNode)
              }));
            }
            
            if (updatedNode.split.categoryMap) {
              updatedNode.split.categoryMap = updatedNode.split.categoryMap.map((category: any) => ({
                ...category,
                childNode: replaceNodeInTree(category.childNode, targetNode, replacementNode)
              }));
            }
            
            if (updatedNode.split.trueChild) {
              updatedNode.split.trueChild = replaceNodeInTree(updatedNode.split.trueChild, targetNode, replacementNode);
            }
            
            if (updatedNode.split.falseChild) {
              updatedNode.split.falseChild = replaceNodeInTree(updatedNode.split.falseChild, targetNode, replacementNode);
            }
          }
          
          return updatedNode;
        };
        
        // Replace the parentNode with updatedParentNode in the copied tree
        const finalTree = replaceNodeInTree(copiedNode, parentNode, updatedParentNode);
        return finalTree;
      }
    }

    return copiedNode;
  };

  const handleCreateSplit = (rowIndex: number, colIndex: number, newValue: string) => {
    const column = inputColumns[colIndex];
    const targetPath = paths[rowIndex];
    const newTree = addSplitToSpecificPathLocal(decisionTree, targetPath, colIndex, column, newValue);
    
    if (newTree) {
      onUpdate({
        ...table,
        decisionTree: newTree
      });
    }
  };

  const handleEditSplit = (rowIndex: number, colIndex: number, newValue: string) => {
    const column = inputColumns[colIndex];
    const targetPath = paths[rowIndex];
    const newTree = updateSplitInSpecificPath(decisionTree, targetPath, colIndex, column, newValue);
    
    if (newTree) {
      onUpdate({
        ...table,
        decisionTree: newTree
      });
    }
  };

  const handleDeleteBranch = (rowIndex: number, colIndex: number) => {
    const targetPath = paths[rowIndex];
    const newTree = deleteBranchFromSpecificPath(decisionTree, targetPath, colIndex);
    
    if (newTree) {
      onUpdate({
        ...table,
        decisionTree: newTree
      });
    }
  };

  const closeLeafEditor = () => {
    setIsLeafEditorOpen(false);
    setEditingPathIndex(null);
    setEditingOutput(null);
  };

  const closeColumnModal = () => {
    setIsColumnModalOpen(false);
  };

  return {
    // State
    isLeafEditorOpen,
    editingPathIndex,
    editingOutput,
    isColumnModalOpen,
    
    // Paths
    paths,
    
    // Handlers
    handleEditOutput,
    handleSaveOutput,
    handleCreateSplit,
    handleEditSplit,
    handleDeleteBranch,
    handleAddColumn,
    handleDeleteColumn,
    handleAddNewColumn,
    
    // Modal controls
    closeLeafEditor,
    closeColumnModal
  };
}
